import React from "react"
import PropTypes from "prop-types"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import {Box, Divider, Grid, IconButton, Typography} from "@mui/material"
import ButtonWithIcon from "src/v2/components/Button/Button"
import {ReactComponent as CheckIcon} from "src/v2/assets/icons/check-icon.svg"
import theme from "src/v2/theme/theme"
import {normalizeHref} from "src/v2/utils/normalize-href"
import SummarySkeletonGrid from "../../pages/DatalakeEmissionv2/components/summary-skeleton/summary-skeleton"

const SummaryItem = ({label, value, externalLink, externalLinkLabel}) => {
    const normalizedExternalLink = normalizeHref(externalLink)

    return (
        <Box display="flex" gap="5px" flexDirection="column">
            <Typography
                variant="contraHeading"
                sx={{color: theme.palette.primary.white70}}
            >
                {label}
            </Typography>

            <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
                <Typography
                    variant="textBody"
                    color="common.white"
                    sx={{whiteSpace: "pre-line", minWidth: 0, overflowWrap: "anywhere"}}
                >
                    {value ?? "-"}
                </Typography>

                {normalizedExternalLink && (
                    <IconButton
                        aria-label={externalLinkLabel ?? `Abrir ${label}`}
                        component="a"
                        href={normalizedExternalLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        sx={{
                            color: theme.palette.icon.main,
                            flexShrink: 0,
                            "&:focus-visible": {
                                outline: `2px solid ${theme.palette.common.white}`,
                                outlineOffset: 2,
                            },
                        }}
                    >
                        <OpenInNewIcon fontSize="small"/>
                    </IconButton>
                )}
            </Box>
        </Box>
    )
}

SummaryItem.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.node,
    ]),
    externalLink: PropTypes.string,
    externalLinkLabel: PropTypes.string,
}

const EmptySummaryItem = ({text}) => {
    return (
        <Typography variant="textBody" color="common.white">
            {text}
        </Typography>
    )
}

EmptySummaryItem.propTypes = {
    text: PropTypes.string.isRequired,
}

const SummarySection = ({
                            title,
                            fields,
                            empty,
                            emptyText,
                            onClick,
                            fieldGridProps,
                            loading,
                        }) => {
    const canClick = !loading && Boolean(onClick)

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "row",
                height: "100%",
                width: "100%",
            }}
        >
            <Divider
                orientation="vertical"
                flexItem
                sx={{
                    width: 2,
                    mr: "22px",
                    py: 3,
                    backgroundColor: theme.palette.common.white,
                    border: "none",
                    "&::before, &::after": {border: "none"},
                }}
            />

            <Box
                sx={{
                    flex: 1,
                    gap: 2,
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 0,
                }}
            >
                <Typography variant="textLarge" color="common.white">
                    {title}
                </Typography>

                {loading ? (
                    <SummarySkeletonGrid fields={fields}/>
                ) : empty ? (
                    <EmptySummaryItem text={emptyText}/>
                ) : (
                    <Grid container spacing={2}>
                        {fields.map((field) => (
                            <Grid
                                key={field.label}
                                item
                                {...(field.gridProps ?? fieldGridProps)}
                            >
                                <SummaryItem
                                    label={field.label}
                                    value={field.value}
                                    externalLink={field.externalLink}
                                    externalLinkLabel={field.externalLinkLabel}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}

                <Box
                    sx={{
                        maxWidth: "344px",
                        opacity: loading ? 0.5 : 1,
                        pointerEvents: loading ? "none" : "auto",
                    }}
                >
                    <ButtonWithIcon
                        onClick={canClick ? onClick : undefined}
                        icon={<CheckIcon/>}
                        disabled={loading}
                    >
                        MOSTRAR TODOS
                    </ButtonWithIcon>
                </Box>
            </Box>
        </Box>
    )
}

SummarySection.propTypes = {
    title: PropTypes.string.isRequired,
    fields: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
                PropTypes.node,
            ]),
            gridProps: PropTypes.object,
            externalLink: PropTypes.string,
            externalLinkLabel: PropTypes.string,
        })
    ),
    empty: PropTypes.bool,
    emptyText: PropTypes.string,
    onClick: PropTypes.func,
    fieldGridProps: PropTypes.object,
    loading: PropTypes.bool,
}

SummarySection.defaultProps = {
    fields: [],
    empty: false,
    emptyText: "",
    onClick: null,
    fieldGridProps: {
        xxxs: 4,
        sm: "auto",
    },
    loading: false,
}

export default SummarySection
